import * as THREE from 'three';

export class World {
    constructor(scene) {
        this.scene = scene;
        this.mushrooms = []; // Store mushroom groups for collision detection
        
        this.createLighting();
        this.createGround();
        this.createEnvironment();
        this.createSkybox();
    }
    
    createLighting() {
        // Ambient light for overall illumination
        const ambientLight = new THREE.AmbientLight(0x404040, 1.5);
        this.scene.add(ambientLight);
        
        // Directional light (sun) for shadows
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(50, 100, 50);
        directionalLight.castShadow = true;
        directionalLight.shadow.camera.left = -50;
        directionalLight.shadow.camera.right = 50;
        directionalLight.shadow.camera.top = 50;
        directionalLight.shadow.camera.bottom = -50;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        this.scene.add(directionalLight);
        
        // Psychedelic point lights around the scene
        const colors = [0xff00ff, 0x00ffff, 0xffff00, 0xff0088];
        for (let i = 0; i < 4; i++) {
            const light = new THREE.PointLight(colors[i], 2, 30);
            const angle = (i / 4) * Math.PI * 2;
            light.position.set(
                Math.cos(angle) * 25,
                5,
                Math.sin(angle) * 25
            );
            this.scene.add(light);
        }
    }
    
    createGround() {
        // BIGGER ISLAND with beach and ocean!
        
        // 1. Ocean (extends to horizon - 2000x2000)
        const oceanGeometry = new THREE.PlaneGeometry(2000, 2000, 100, 100);
        const oceanMaterial = new THREE.MeshStandardMaterial({
            color: 0x0066aa,
            roughness: 0.3,
            metalness: 0.6,
            transparent: true,
            opacity: 0.9
        });
        
        // Add waves to ocean
        const oceanVertices = oceanGeometry.attributes.position.array;
        for (let i = 0; i < oceanVertices.length; i += 3) {
            const x = oceanVertices[i];
            const y = oceanVertices[i + 1];
            // Wave pattern
            oceanVertices[i + 2] = Math.sin(x * 0.01) * 0.5 + Math.cos(y * 0.01) * 0.5;
        }
        oceanGeometry.attributes.position.needsUpdate = true;
        oceanGeometry.computeVertexNormals();
        
        this.ocean = new THREE.Mesh(oceanGeometry, oceanMaterial);
        this.ocean.rotation.x = -Math.PI / 2;
        this.ocean.position.y = -3; // Raised up by 1 unit (was -4)
        this.ocean.receiveShadow = true;
        this.scene.add(this.ocean);
        
        // 2. Main island (270x270 - smaller to make room for beach)
        const islandGeometry = new THREE.PlaneGeometry(270, 270, 80, 80);
        const islandMaterial = new THREE.MeshStandardMaterial({
            color: 0x228B22, // Grass green
            roughness: 0.9,
            metalness: 0.1
        });
        
        // Make terrain bumpy and elevated
        const islandVertices = islandGeometry.attributes.position.array;
        for (let i = 0; i < islandVertices.length; i += 3) {
            const x = islandVertices[i];
            const y = islandVertices[i + 1];
            const distFromCenter = Math.sqrt(x * x + y * y);
            
            // Gentle hills across island
            let height = Math.random() * 2 - 0.5;
            
            // Slope down towards edges for beach transition (smoother!)
            if (distFromCenter > 90) {
                // More gradual slope from island to beach
                const slopeFactor = (distFromCenter - 90) / 45; // Gentler
                height -= slopeFactor * 4; // Smooth descent to beach level
            }
            
            islandVertices[i + 2] = height;
        }
        islandGeometry.attributes.position.needsUpdate = true;
        islandGeometry.computeVertexNormals();
        
        const island = new THREE.Mesh(islandGeometry, islandMaterial);
        island.rotation.x = -Math.PI / 2;
        island.receiveShadow = true;
        island.castShadow = true;
        this.scene.add(island);
        this.island = island; // Store for collision detection
        
        // 3. Beach ring with SLOPE (sandy area between island and ocean)
        // Use plane geometry with slope instead of flat ring
        const beachGeometry = new THREE.PlaneGeometry(340, 340, 64, 64);
        const beachMaterial = new THREE.MeshStandardMaterial({
            color: 0xf4e4c1, // Sandy color
            roughness: 0.95,
            metalness: 0.05
        });
        
        // Create sloped beach - starts below green, tapers at 9 degrees for long distance
        const beachVertices = beachGeometry.attributes.position.array;
        for (let i = 0; i < beachVertices.length; i += 3) {
            const x = beachVertices[i];
            const y = beachVertices[i + 1];
            const distFromCenter = Math.sqrt(x * x + y * y);
            
            let height = 0;
            
            // Beach starts at radius 135 at -2.5 (below island at 0)
            // Tapers at 9 degrees: tan(9°) ≈ 0.158
            if (distFromCenter >= 135 && distFromCenter <= 300) {
                const distFromBeachStart = distFromCenter - 135;
                // 9-degree slope downward
                height = -2.5 - (distFromBeachStart * 0.158);
            } else if (distFromCenter < 135) {
                // Inside island area - hide beach (lower it far down)
                height = -20; // Far below island
            } else {
                // Past beach end - continue sloping to water
                height = -2.5 - (165 * 0.158); // Continue from where beach ended
            }
            
            beachVertices[i + 2] = height;
        }
        beachGeometry.attributes.position.needsUpdate = true;
        beachGeometry.computeVertexNormals();
        
        this.beach = new THREE.Mesh(beachGeometry, beachMaterial);
        this.beach.rotation.x = -Math.PI / 2;
        this.beach.receiveShadow = true;
        this.scene.add(this.beach);
        
        // 4. VOLCANO in center!
        this.createVolcano();
    }
    
    createVolcano() {
        const volcanoGroup = new THREE.Group();
        
        // Create CLIMBABLE volcano terrain using plane geometry with heightmap
        const segments = 64;
        const volcanoRadius = 20;
        const volcanoHeight = 30;
        
        // Main volcano slope (climbable terrain)
        const volcanoGeometry = new THREE.PlaneGeometry(volcanoRadius * 2, volcanoRadius * 2, segments, segments);
        const volcanoMaterial = new THREE.MeshStandardMaterial({
            color: 0x4a3c3c, // Dark grey/brown rock
            roughness: 0.95,
            metalness: 0.1,
            side: THREE.DoubleSide
        });
        
        // Create cone-shaped heightmap (climbable!)
        const vertices = volcanoGeometry.attributes.position.array;
        for (let i = 0; i < vertices.length; i += 3) {
            const x = vertices[i];
            const z = vertices[i + 1];
            const distFromCenter = Math.sqrt(x * x + z * z);
            
            // Cone formula: height decreases linearly from center
            if (distFromCenter < volcanoRadius) {
                // Smooth cone slope
                const normalizedDist = distFromCenter / volcanoRadius;
                const height = volcanoHeight * (1 - normalizedDist);
                
                // Crater depression at top
                if (distFromCenter < 6) {
                    const craterDepth = (6 - distFromCenter) * 2;
                    vertices[i + 2] = height - craterDepth;
                } else {
                    vertices[i + 2] = height;
                }
                
                // Add rocky texture with noise
                vertices[i + 2] += (Math.random() - 0.5) * 0.8;
            } else {
                vertices[i + 2] = 0; // Flat at edges
            }
        }
        
        volcanoGeometry.attributes.position.needsUpdate = true;
        volcanoGeometry.computeVertexNormals();
        
        const volcanoTerrain = new THREE.Mesh(volcanoGeometry, volcanoMaterial);
        volcanoTerrain.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        volcanoTerrain.position.y = 0;
        volcanoTerrain.receiveShadow = true;
        volcanoTerrain.castShadow = true;
        volcanoGroup.add(volcanoTerrain);
        
        // Lava pool at crater (on volcano surface)
        const lavaGeometry = new THREE.CircleGeometry(5, 32);
        const lavaMaterial = new THREE.MeshStandardMaterial({
            color: 0xff4400,
            emissive: 0xff2200,
            emissiveIntensity: 1.5,
            roughness: 0.3
        });
        this.lava = new THREE.Mesh(lavaGeometry, lavaMaterial);
        this.lava.rotation.x = -Math.PI / 2;
        this.lava.position.y = 18; // On top of volcano crater floor (depressed by ~12 from 30)
        volcanoGroup.add(this.lava);
        
        // Store lava position and radius for damage detection
        this.lavaPosition = new THREE.Vector3(0, 18, 0);
        this.lavaRadius = 5;
        
        // Lava light
        const lavaLight = new THREE.PointLight(0xff4400, 3, 50);
        lavaLight.position.y = 26;
        volcanoGroup.add(lavaLight);
        
        // SMOKE PARTICLES rising from crater!
        const smokeParticles = [];
        const smokeCount = 50;
        
        for (let i = 0; i < smokeCount; i++) {
            const smokeGeometry = new THREE.SphereGeometry(0.5 + Math.random() * 1, 8, 8);
            const smokeMaterial = new THREE.MeshBasicMaterial({
                color: 0x444444,
                transparent: true,
                opacity: 0.3 + Math.random() * 0.3
            });
            const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
            
            // Random position around crater
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * 4;
            smoke.position.x = Math.cos(angle) * radius;
            smoke.position.z = Math.sin(angle) * radius;
            smoke.position.y = 25 + Math.random() * 10; // Start at crater, rise up
            
            smoke.userData = {
                riseSpeed: 1 + Math.random() * 2, // Random rise speed
                sideSpeed: (Math.random() - 0.5) * 0.5, // Drift sideways
                startY: smoke.position.y,
                maxY: 45 // Rise to 45 units
            };
            
            smokeParticles.push(smoke);
            volcanoGroup.add(smoke);
        }
        
        // Store smoke for animation
        volcanoGroup.userData.smokeParticles = smokeParticles;
        
        // WARM RED/ORANGE GLOW around crater
        const glowLight1 = new THREE.PointLight(0xff3300, 2, 30);
        glowLight1.position.set(0, 25, 0);
        volcanoGroup.add(glowLight1);
        
        const glowLight2 = new THREE.PointLight(0xff6600, 1.5, 25);
        glowLight2.position.set(3, 27, 3);
        volcanoGroup.add(glowLight2);
        
        const glowLight3 = new THREE.PointLight(0xff4400, 1.5, 25);
        glowLight3.position.set(-3, 27, -3);
        volcanoGroup.add(glowLight3);
        
        // Add some volcanic rocks around base
        for (let i = 0; i < 20; i++) {
            const angle = (i / 20) * Math.PI * 2;
            const radius = volcanoRadius + Math.random() * 3;
            const rockSize = 0.5 + Math.random() * 1.5;
            
            const rockGeometry = new THREE.DodecahedronGeometry(rockSize, 0);
            const rockMaterial = new THREE.MeshStandardMaterial({
                color: 0x3a2a2a,
                roughness: 0.95
            });
            const rock = new THREE.Mesh(rockGeometry, rockMaterial);
            rock.position.set(
                Math.cos(angle) * radius,
                rockSize / 2,
                Math.sin(angle) * radius
            );
            rock.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            rock.castShadow = true;
            volcanoGroup.add(rock);
        }
        
        // Position at world center
        volcanoGroup.position.set(0, 0, 0);
        this.scene.add(volcanoGroup);
        
        // Store for damage check and Lez healing
        this.volcano = {
            group: volcanoGroup,
            terrain: volcanoTerrain,
            position: new THREE.Vector3(0, 0, 0),
            craterHeight: 25,
            damageRadius: 12,
            healRadius: 8,
            radius: volcanoRadius,
            height: volcanoHeight
        };
        
        console.log('🌋 Climbable volcano terrain created! You can walk up the slopes!');
    }
    
    createEnvironment() {
        // Create mushrooms (Chooma Island signature)
        this.createMushrooms();
        
        // Create trees
        this.createTrees();
        
        // Create rocks
        this.createRocks();
        
        // Create clouds
        this.clouds = [];
        this.createClouds();
    }
    
    getTerrainHeightAt(position) {
        // Raycast down to find terrain height
        const raycaster = new THREE.Raycaster();
        const rayOrigin = new THREE.Vector3(position.x, 100, position.z);
        const rayDirection = new THREE.Vector3(0, -1, 0);
        raycaster.set(rayOrigin, rayDirection);
        
        const terrainMeshes = [];
        if (this.island) terrainMeshes.push(this.island);
        if (this.volcano && this.volcano.terrain) terrainMeshes.push(this.volcano.terrain);
        if (this.beach) terrainMeshes.push(this.beach);
        
        const intersects = raycaster.intersectObjects(terrainMeshes, false);
        return intersects.length > 0 ? Math.max(...intersects.map(hit => hit.point.y)) : 0;
    }
    
    spawnMushroom(position) {
        // Spawn a single mushroom at the given position
        const mushroomGroup = this.createSingleMushroom();
        
        // Get terrain height at position (works on volcano too!)
        const terrainHeight = this.getTerrainHeightAt(position);
        
        mushroomGroup.position.set(position.x, terrainHeight, position.z);
        
        this.scene.add(mushroomGroup);
        this.mushrooms.push(mushroomGroup);
        
        return mushroomGroup;
    }
    
    createSingleMushroom() {
        // Create a single mushroom (extracted from createMushrooms)
        const mushroomGroup = new THREE.Group();
        
        const scale = 0.5 + Math.random() * 0.5;
        
        // Stem - Made taller to properly connect with cap
        const stemGeometry = new THREE.CylinderGeometry(0.2, 0.3, 2.5, 8);
        const stemMaterial = new THREE.MeshStandardMaterial({
            color: 0xf5f5dc,
            roughness: 0.7
        });
        const stem = new THREE.Mesh(stemGeometry, stemMaterial);
        stem.castShadow = true;
        stem.position.y = 1.25; // Position stem properly
        mushroomGroup.add(stem);
        
        // Cap with spotted texture
        const capGeometry = new THREE.SphereGeometry(1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
        const capColor = Math.random() > 0.5 ? 0xff0000 : 0xff00ff;
        
        // Create texture with white spots
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        
        // Base color
        ctx.fillStyle = capColor === 0xff0000 ? '#ff0000' : '#ff00ff';
        ctx.fillRect(0, 0, 512, 512);
        
        // Add white spots (8-12 spots covering ~25% area)
        ctx.fillStyle = '#ffffff';
        const numTextureSpots = 8 + Math.floor(Math.random() * 5);
        for (let s = 0; s < numTextureSpots; s++) {
            const x = Math.random() * 512;
            const y = Math.random() * 512;
            const radius = 20 + Math.random() * 40;
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
        }
        
        const texture = new THREE.CanvasTexture(canvas);
        
        const capMaterial = new THREE.MeshStandardMaterial({
            map: texture,
            color: 0xffffff, // Use white to show true texture colors
            emissive: capColor,
            emissiveIntensity: 0.3, // Brighter glow for visibility
            roughness: 0.5
        });
        const cap = new THREE.Mesh(capGeometry, capMaterial);
        cap.position.y = 2.5; // Raised to connect with stem
        cap.castShadow = true;
        mushroomGroup.add(cap);
        
        // Store cap reference for pulsing animation
        mushroomGroup.userData.cap = cap;
        mushroomGroup.userData.baseEmissive = 0.3;
        
        // White spots on cap (5-8 spots, covering ~30% max)
        const numSpots = 5 + Math.floor(Math.random() * 4);
        for (let j = 0; j < numSpots; j++) {
            const spotSize = 0.1 + Math.random() * 0.15;
            const spotGeometry = new THREE.SphereGeometry(spotSize, 8, 8);
            const spotMaterial = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                roughness: 0.3
            });
            const spot = new THREE.Mesh(spotGeometry, spotMaterial);
            
            // Random position on cap surface
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * 0.7;
            const height = Math.random() * 0.3;
            
            spot.position.set(
                Math.cos(angle) * radius,
                2.5 + height,
                Math.sin(angle) * radius
            );
            
            mushroomGroup.add(spot);
        }
        
        mushroomGroup.scale.set(scale, scale, scale);
        
        // Store reference for collision detection
        mushroomGroup.userData.isPickup = true;
        mushroomGroup.userData.collected = false;
        
        return mushroomGroup;
    }
    
    createMushrooms() {
        for (let i = 0; i < 20; i++) {
            const mushroomGroup = this.createSingleMushroom();
            
            // Random position
            const x = (Math.random() - 0.5) * 80;
            const z = (Math.random() - 0.5) * 80;
            mushroomGroup.position.set(x, 0, z);
            
            this.scene.add(mushroomGroup);
            this.mushrooms.push(mushroomGroup);
        }
    }
    
    createTrees() {
        // MANY trees! 160 for dense forest
        for (let i = 0; i < 160; i++) {
            // Random position - spread across larger area
            const x = (Math.random() - 0.5) * 180; // Bigger spread
            const z = (Math.random() - 0.5) * 180;
            
            // Check distance from volcano (at 0, 0)
            const distFromVolcano = Math.sqrt(x * x + z * z);
            
            // Don't place trees near volcano (25 unit radius clear zone)
            if (distFromVolcano < 25) {
                i--; // Try again
                continue;
            }
            
            const treeGroup = new THREE.Group();
            
            // Trunk - vary sizes for realism
            const trunkHeight = 4 + Math.random() * 3;
            const trunkRadius = 0.4 + Math.random() * 0.3;
            const trunkGeometry = new THREE.CylinderGeometry(trunkRadius, trunkRadius * 1.2, trunkHeight, 8);
            const trunkMaterial = new THREE.MeshStandardMaterial({
                color: 0x8B4513,
                roughness: 0.9
            });
            const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
            trunk.castShadow = true;
            treeGroup.add(trunk);
            
            // Foliage - vary sizes
            const foliageSize = 1.5 + Math.random() * 1.5;
            const foliageGeometry = new THREE.SphereGeometry(foliageSize, 8, 8);
            const foliageMaterial = new THREE.MeshStandardMaterial({
                color: 0x228B22, // Forest green
                roughness: 0.8
            });
            const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
            // Position foliage at TOP of trunk
            foliage.position.y = trunkHeight + (foliageSize * 0.3);
            foliage.castShadow = true;
            treeGroup.add(foliage);
            
            // Position tree at ground level (y=0 for base)
            treeGroup.position.set(x, 0, z);
            
            // Move trunk up so base is at y=0
            trunk.position.y = trunkHeight / 2;
            
            // Random rotation for variety
            treeGroup.rotation.y = Math.random() * Math.PI * 2;
            
            this.scene.add(treeGroup);
        }
        
        console.log('🌲 Created 160 trees across the island!');
    }
    
    createRocks() {
        for (let i = 0; i < 30; i++) {
            const size = 0.5 + Math.random() * 1.5;
            const geometry = new THREE.DodecahedronGeometry(size, 0);
            const material = new THREE.MeshStandardMaterial({
                color: 0x808080,
                roughness: 0.9,
                metalness: 0.1
            });
            
            const rock = new THREE.Mesh(geometry, material);
            rock.position.set(
                (Math.random() - 0.5) * 90,
                size / 2,
                (Math.random() - 0.5) * 90
            );
            rock.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            rock.castShadow = true;
            rock.receiveShadow = true;
            
            this.scene.add(rock);
        }
    }
    
    createClouds() {
        // Create 15-20 fluffy clouds floating in the sky
        const numClouds = 15 + Math.floor(Math.random() * 6);
        
        for (let i = 0; i < numClouds; i++) {
            const cloudGroup = new THREE.Group();
            
            // Each cloud is made of 3-5 spheres clustered together
            const numPuffs = 3 + Math.floor(Math.random() * 3);
            
            for (let j = 0; j < numPuffs; j++) {
                const size = 2 + Math.random() * 3;
                const geometry = new THREE.SphereGeometry(size, 8, 8);
                const material = new THREE.MeshStandardMaterial({
                    color: 0xffffff,
                    transparent: true,
                    opacity: 0.7 + Math.random() * 0.2,
                    roughness: 1.0,
                    metalness: 0
                });
                
                const puff = new THREE.Mesh(geometry, material);
                puff.position.set(
                    (Math.random() - 0.5) * 4,
                    (Math.random() - 0.5) * 2,
                    (Math.random() - 0.5) * 4
                );
                
                cloudGroup.add(puff);
            }
            
            // Position clouds spread 4x further (1600 units)
            const xPos = (Math.random() - 0.5) * 1600;
            const zPos = (Math.random() - 0.5) * 1600;
            
            // Calculate distance from center for height scaling
            const distFromCenter = Math.sqrt(xPos * xPos + zPos * zPos);
            const maxDist = 800; // Half of 1600
            
            // Height decreases towards edges to touch water at horizon
            // Center clouds: 60-140 high, edge clouds: 0-20 high (touching water at -4)
            const distRatio = Math.min(distFromCenter / maxDist, 1.0);
            const minHeight = 60 - (distRatio * 50); // Decreases from 60 to 10
            const heightRange = 80 - (distRatio * 70); // Range decreases from 80 to 10
            
            cloudGroup.position.set(
                xPos,
                minHeight + Math.random() * heightRange,
                zPos
            );
            
            // Scale clouds based on height (farther = bigger to maintain visibility)
            const heightFactor = 1 + (cloudGroup.position.y - 30) / 80;
            cloudGroup.scale.setScalar(heightFactor * (1 + distRatio * 0.5));
            
            // Random slow drift speed
            cloudGroup.userData.driftSpeed = {
                x: (Math.random() - 0.5) * 0.5,
                z: (Math.random() - 0.5) * 0.5
            };
            
            this.scene.add(cloudGroup);
            this.clouds.push(cloudGroup);
        }
    }
    
    createSkybox() {
        // Psychedelic gradient sky
        const skyGeometry = new THREE.SphereGeometry(500, 32, 32);
        const skyMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 }
            },
            vertexShader: `
                varying vec3 vWorldPosition;
                void main() {
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vWorldPosition = worldPosition.xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                varying vec3 vWorldPosition;
                void main() {
                    vec3 direction = normalize(vWorldPosition);
                    float h = direction.y * 0.5 + 0.5;
                    
                    vec3 topColor = vec3(0.5, 0.0, 0.8);
                    vec3 bottomColor = vec3(0.0, 0.5, 0.8);
                    
                    vec3 color = mix(bottomColor, topColor, h);
                    gl_FragColor = vec4(color, 1.0);
                }
            `,
            side: THREE.BackSide
        });
        
        const sky = new THREE.Mesh(skyGeometry, skyMaterial);
        this.scene.add(sky);
        this.skyMaterial = skyMaterial;
    }
    
    update(deltaTime) {
        // Animate sky
        if (this.skyMaterial) {
            this.skyMaterial.uniforms.time.value += deltaTime;
        }
        
        // Animate ocean waves
        if (this.ocean) {
            const time = Date.now() / 1000;
            const vertices = this.ocean.geometry.attributes.position.array;
            for (let i = 0; i < vertices.length; i += 3) {
                const x = vertices[i];
                const y = vertices[i + 1];
                // Moving wave pattern
                vertices[i + 2] = Math.sin(x * 0.01 + time) * 0.5 + Math.cos(y * 0.01 + time) * 0.5;
            }
            this.ocean.geometry.attributes.position.needsUpdate = true;
        }
        
        // Pulse mushrooms to make them visible
        const time = Date.now() / 1000;
        this.mushrooms.forEach(mushroom => {
            if (!mushroom.userData.collected && mushroom.userData.cap) {
                const pulse = Math.sin(time * 2) * 0.15 + 0.3; // Pulse between 0.15 and 0.45
                mushroom.userData.cap.material.emissiveIntensity = pulse;
            }
        });
        
        // Animate volcano smoke
        if (this.volcano && this.volcano.group.userData.smokeParticles) {
            this.volcano.group.userData.smokeParticles.forEach(smoke => {
                // Rise up
                smoke.position.y += smoke.userData.riseSpeed * deltaTime;
                
                // Drift sideways
                smoke.position.x += smoke.userData.sideSpeed * deltaTime;
                
                // Fade out as it rises
                const heightProgress = (smoke.position.y - 25) / 20; // 0 to 1 over 20 units
                smoke.material.opacity = Math.max(0, 0.6 - heightProgress * 0.6);
                
                // Scale up as it rises
                const scale = 1 + heightProgress * 2;
                smoke.scale.set(scale, scale, scale);
                
                // Reset when too high
                if (smoke.position.y > smoke.userData.maxY) {
                    const angle = Math.random() * Math.PI * 2;
                    const radius = Math.random() * 4;
                    smoke.position.x = Math.cos(angle) * radius;
                    smoke.position.z = Math.sin(angle) * radius;
                    smoke.position.y = 25 + Math.random() * 3;
                    smoke.scale.set(1, 1, 1);
                    smoke.material.opacity = 0.3 + Math.random() * 0.3;
                }
            });
        }
        
        // Animate clouds - slow drift
        this.clouds.forEach(cloud => {
            cloud.position.x += cloud.userData.driftSpeed.x * deltaTime;
            cloud.position.z += cloud.userData.driftSpeed.z * deltaTime;
            
            // Wrap around if too far (bigger boundaries for bigger area)
            if (cloud.position.x > 200) cloud.position.x = -200;
            if (cloud.position.x < -200) cloud.position.x = 200;
            if (cloud.position.z > 200) cloud.position.z = -200;
            if (cloud.position.z < -200) cloud.position.z = 200;
        });
    }
}
